import React, { memo, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { Map } from 'immutable';
import { ContextMenu, ContextMenuTrigger, MenuItem } from 'react-contextmenu';

import Table, { Column, Row } from '../../../helpers/Table';
import { Tooltip } from '../../../helpers/Tooltip';
import Input from '../../../helpers/Input';
import Span from '../../../helpers/Span';
import { IconButton } from '../../../helpers/IconButton';
import uniqueId from '../../../../utils/uniqueId';
import { noop } from '../../../../utils/noop';
import { usePrettyErrorContext } from '../../../../contexts/PrettyErrorContext';
import { useAddNewIndustryContext } from '../../../../contexts/AddNewIndustryContext';

import style from './style.module.css';

export default function CreateTable({ namePrefix: NAME_PREFIX, tableName: TABLE_NAME, menu = true, columns: COLUMNS }) {
  const hasAddNewColumn = COLUMNS.some(column => column.addNew);

  const ContextMenuWrapper = onClick => (value, data, isDisabled) => {
    if (!menu) {
      return (
        <span
          className={classNames({
            'text-danger': data.get('deprecated', false) || data.get('deletedAt'),
          })}
        >
          {value}
        </span>
      );
    }

    const id = uniqueId();

    return (
      <div>
        <ContextMenuTrigger disable={isDisabled} id={id}>
          {value}
        </ContextMenuTrigger>

        <ContextMenu id={id}>
          <MenuItem data={data.toJS()} onClick={onClick}>
            <i className="fa fa-remove" /> Delete
          </MenuItem>
        </ContextMenu>
      </div>
    );
  };

  const getInput = ({ type, value, onChange, i, field, suggest, suggestCreate, columnIndex, addNew, ...rest }) => {
    if (suggest) return <div {...rest}>{suggestCreate(NAME_PREFIX, i, field, columnIndex, addNew, value)}</div>;

    return (
      <Input
        {...rest}
        autoFocus={columnIndex === 0}
        name={`${NAME_PREFIX}.${i}.${field}`}
        onChange={onChange}
        placeholder=""
        type={type}
        value={value}
      />
    );
  };

  const Wrapper = ({
    onChange,
    onDelete,
    field,
    type = 'text',
    getClassName,
    suggest,
    suggestCreate,
    columnIndex,
    editable,
    style,
    addNew,
    inputErrors,
    disabled,
  }) => (value, data, i) => {
    const handleDelete = (...rest) => {
      onDelete(...rest, `${NAME_PREFIX}.${i}.${field}`, i);
    };

    let cls = '';

    if (getClassName) cls = getClassName(value, data, i);

    let content = null;

    if (editable && data.get('isEditing')) {
      content = getInput({
        value,
        type,
        onChange,
        i,
        field,
        suggest,
        suggestCreate,
        columnIndex,
        addNew,
        inputErrors,
      });
    } else {
      content = (
        <Span checkOn="children" className={cls} name={`${NAME_PREFIX}.${i}.${field}`} type={type} minSizes>
          {value}
        </Span>
      );
    }

    return (
      <div style={style}>
        <Tooltip content={inputErrors.get(`${NAME_PREFIX}.${i}.${field}`, '')} name={`${NAME_PREFIX}.${i}.${field}`}>
          {ContextMenuWrapper(handleDelete)(content, data, disabled)}
        </Tooltip>
      </div>
    );
  };

  const CompanyDetailsTableComponent = props => {
    const {
      data,
      onAdd,
      onChange,
      onClick,
      onSort = noop,
      onDelete,
      extraClassName = '',
      sortedBy = null,
      order = 0,
      disabled = false,
      canEditData,
      suggestCreate,
      loading = false,
    } = props;

    const { inputErrors = Map() } = usePrettyErrorContext();
    const { addNewIndustry: addNewIndustryFromContext } = useAddNewIndustryContext();
    const addNewIndustry = hasAddNewColumn ? addNewIndustryFromContext : undefined;

    const columnsWidthRef = useRef([]);
    const focusElementNameRef = useRef('');
    const divRef = useRef(null);

    useEffect(() => {
      if (focusElementNameRef.current) {
        const input = document.getElementsByName(focusElementNameRef.current);

        if (input && input.length > 0) {
          input[0].focus();
        }
        focusElementNameRef.current = '';
      }
    });

    const rememberWidth = useCallback((node, index) => {
      if (node) {
        columnsWidthRef.current[index] = node.offsetWidth;
      }
    }, []);

    const getColumnWidth = index => columnsWidthRef.current[index] || 'auto';

    const checkCanEditData = useCallback(
      namePrefix => {
        if (disabled) return false;
        if (!canEditData && namePrefix === 'harvcoTags') return true;

        return canEditData;
      },
      [disabled, canEditData],
    );

    const handleCellClick = useCallback(
      (name, NAME_PREFIX, i) => {
        const canEdit = checkCanEditData(NAME_PREFIX);

        if (!canEdit) return;
        focusElementNameRef.current = `${NAME_PREFIX}.${i}.${name}`;
        onClick(name, NAME_PREFIX, i);
      },
      [checkCanEditData, onClick],
    );

    let suggestOne = null;

    const columns = COLUMNS.map((column, i) => {
      const {
        className: cls,
        field,
        title,
        type,
        suggest,
        editable = true,
        getClassName,
        maxWidth = 'unset',
        ...props
      } = column;
      const className = classNames(cls, {
        'plus-icon': i === 0,
      });
      const columnStyle = { maxWidth, width: getColumnWidth(i) };

      suggestOne = suggestOne || suggest;

      const titleContent = (function() {
        const t = i === 0 ? title || TABLE_NAME : title;

        if (field === sortedBy) {
          if (order === 1) {
            return (
              <span>
                {t}
                <i className="fa fa-caret-down" />
              </span>
            );
          }

          return (
            <span>
              {t}
              <i className="fa fa-caret-up" />
            </span>
          );
        }

        return <span>{t}</span>;
      })();

      let realTitle = (
        <div ref={node => rememberWidth(node, i)} onClick={event => onSort(event, column)} style={columnStyle}>
          {titleContent}
        </div>
      );
      const canEdit = checkCanEditData(NAME_PREFIX);

      if (i === 0) {
        realTitle = (
          <div ref={node => rememberWidth(node, i)} onClick={event => onSort(event, column)} style={columnStyle}>
            <span>
              <IconButton
                className={style.addButton}
                disabled={!canEdit}
                icon="plus"
                onClick={event => {
                  event.preventDefault();
                  if (!disabled) {
                    onAdd(event, NAME_PREFIX);
                  }
                }}
                size="sm"
                title="Add"
                variant="primary"
              />
              {titleContent}
            </span>
          </div>
        );
      }

      const wrapperOptions = {
        editable,
        onChange,
        onDelete: (event, ...rest) => onDelete(event, NAME_PREFIX, ...rest),
        field,
        columnIndex: i,
        type,
        suggest,
        suggestCreate,
        getClassName,
        style: columnStyle,
        addNew: addNewIndustry,
        inputErrors,
        disabled: !canEdit,
      };

      return (
        <Column
          key={`column_${i}`}
          title={realTitle}
          {...props}
          className={className}
          field={field}
          style={columnStyle}
          valueWrapper={Wrapper(wrapperOptions)}
        />
      );
    });

    const canEditRows = checkCanEditData(NAME_PREFIX);

    const rows = data.map((data, i) => (
      <Row
        key={i}
        canEdit={canEditRows}
        checkAlso={['data-suggest', 'canEdit']}
        className="clickable"
        data={data}
        data-suggest={suggestOne}
        onCellClick={name => handleCellClick(name, NAME_PREFIX, i)}
        onChange={onChange}
      />
    ));

    const tableClassName = 'table table-bordered table-hover';
    const formClassName = classNames(style.host, 'table-responsive', extraClassName);

    return (
      <form className={formClassName}>
        <div ref={divRef} className={classNames(style.wrapper, 'w100p')}>
          <Table className={tableClassName} loading={loading}>
            {columns}
            {rows}
          </Table>
        </div>
      </form>
    );
  };

  CompanyDetailsTableComponent.displayName = 'CompanyDetailsTable';

  CompanyDetailsTableComponent.propTypes = {};

  const arePropsEqual = (prevProps, nextProps) =>
    prevProps.data === nextProps.data &&
    prevProps.sortedBy === nextProps.sortedBy &&
    prevProps.order === nextProps.order &&
    prevProps.canEditData === nextProps.canEditData;

  return memo(CompanyDetailsTableComponent, arePropsEqual);
}
